//frontend/src/components/Auth/Login.jsx

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth';
import { Form, Button, Container, Card, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import { useGoogleLogin } from '@react-oauth/google';
import { 
    FaUser, 
    FaLock, 
    FaEye, 
    FaEyeSlash, 
    FaGoogle,
    FaShieldAlt,
    FaHeadset,
    FaBolt,
    FaCheckCircle,
    FaChartLine,
    FaUsers,
    FaClock
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import './Auth.css';

const Login = () => {
    const [credentials, setCredentials] = useState({
        username: '',
        password: '',
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const { login } = useAuth();
    const navigate = useNavigate();

    const handleGoogleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            setLoading(true);
            setError('');
            try {
                const data = await authService.googleLogin(tokenResponse.access_token);
                const userData = data.user;
                toast.success('✅ Connexion Google réussie ! Bienvenue.');
                if (userData.role === 'superadmin') {
                    navigate('/superadmin');
                } else if (userData.role === 'admin') {
                    navigate('/admin');
                } else {
                    navigate('/dashboard');
                }
            } catch (err) {
                setError('Échec de la connexion avec Google. Veuillez réessayer.');
                toast.error('❌ Connexion Google échouée.');
            } finally {
                setLoading(false);
            }
        },
        onError: () => {
            setError('Connexion Google annulée ou échouée.');
            toast.error('❌ Connexion Google échouée.');
        },
    });

    const handleChange = (e) => {
        setCredentials({
            ...credentials,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(credentials);
            
            // Récupérer les infos de l'utilisateur après connexion
            const userData = await authService.getCurrentUser();
            
            // NOUVEAU: Vérifier si l'email est vérifié
            if (!userData.email_verified) {
                setError('Veuillez vérifier votre email avant de vous connecter. Vérifiez votre boîte mail.');
                toast.warning('⚠️ Email non vérifié. Vérifiez votre boîte mail.');
                setLoading(false);
                return;
            }
            
            toast.success('✅ Connexion réussie ! Bienvenue.');
            
            // Redirection selon le rôle
            if (userData.role === 'superadmin') {
                navigate('/superadmin');
            } else if (userData.role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            setError('Identifiants invalides. Veuillez réessayer.');
            toast.error('❌ Identifiants incorrects.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <Container>
                <Row className="justify-content-center py-5">
                    <Col lg={8} xl={10}>
                        <Card className="auth-card shadow-lg">
                            <Row className="g-0">
                                {/* Left Side - Info */}
                                <Col md={6} className="auth-info-section">
                                    <div className="auth-info-content">
                                        <div className="auth-logo">
                                            <h1>🍁 E-Visa Canada</h1>
                                        </div>
                                        <h2 className="auth-info-title">
                                            Bienvenue sur votre espace personnel
                                        </h2>
                                        <p className="auth-info-text">
                                            Accédez à votre tableau de bord pour suivre vos demandes 
                                            de visa et communiquer avec nos conseillers.
                                        </p>
                                        
                                        <div className="auth-features">
                                            <div className="auth-feature">
                                                <FaCheckCircle className="feature-icon" />
                                                <span>Suivi en temps réel de vos dossiers</span>
                                            </div>
                                            <div className="auth-feature">
                                                <FaHeadset className="feature-icon" />
                                                <span>Support client 24/7</span>
                                            </div>
                                            <div className="auth-feature">
                                                <FaShieldAlt className="feature-icon" />
                                                <span>Sécurité maximale de vos données</span>
                                            </div>
                                        </div>

                                        <div className="auth-stats">
                                            <div className="stat-item">
                                                <FaUsers className="stat-icon-svg" />
                                                <h3>15,000+</h3>
                                                <p>Visas approuvés</p>
                                            </div>
                                            <div className="stat-item">
                                                <FaChartLine className="stat-icon-svg" />
                                                <h3>92%</h3>
                                                <p>Taux de succès</p>
                                            </div>
                                            <div className="stat-item">
                                                <FaClock className="stat-icon-svg" />
                                                <h3>4-6 sem</h3>
                                                <p>Délai moyen</p>
                                            </div>
                                        </div>

                                        <div className="trust-badges">
                                            <Container>
                                                <Row className="text-center">
                                                    <Col>
                                                        <div className="trust-badge-item">
                                                            <FaLock className="trust-icon-svg" />
                                                            <p>Paiement sécurisé</p>
                                                        </div>
                                                    </Col>
                                                    <Col>
                                                        <div className="trust-badge-item">
                                                            <FaCheckCircle className="trust-icon-svg" />
                                                            <p>Certifié ISO</p>
                                                        </div>
                                                    </Col>
                                                    <Col>
                                                        <div className="trust-badge-item">
                                                            <FaShieldAlt className="trust-icon-svg" />
                                                            <p>Données protégées</p>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Container>
                                        </div>
                                    </div>
                                </Col>

                                {/* Right Side - Form */}
                                <Col md={6} className="auth-form-section">
                                    <div className="auth-form-content">
                                        <h3 className="auth-form-title">Connexion</h3>
                                        <p className="auth-form-subtitle">
                                            Connectez-vous pour accéder à votre espace
                                        </p>

                                        <div className="auth-social-buttons mb-3">
                                            <Button variant="outline" className="btn-social-auth w-100" onClick={() => handleGoogleLogin()} disabled={loading}>
                                                <FaGoogle className="me-2" />
                                                Continuer avec Google
                                            </Button>
                                        </div>

                                        <div className="auth-divider">
                                            <span>OU</span>
                                        </div>

                                        {error && (
                                            <Alert variant="danger" className="auth-alert">
                                                <strong>Erreur :</strong> {error}
                                            </Alert>
                                        )}

                                        {error && error.includes('email') && (
                                            <div className="text-center mb-3">
                                                <p className="text-muted mb-2">Vous n'avez pas reçu l'email de vérification ?</p>
                                                <Link to="/resend-verification" className="btn btn-outline-primary">
                                                    Renvoyer l'email de vérification
                                                </Link>
                                            </div>
                                        )}
                                                                                
                                        <Form onSubmit={handleSubmit} className="auth-form">
                                            <Form.Group className="mb-4">
                                                <Form.Label className="form-label-custom">
                                                    Nom d'utilisateur ou email
                                                </Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text className="input-icon-group">
                                                        <FaUser />
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        type="text"
                                                        name="username"
                                                        value={credentials.username}
                                                        onChange={handleChange}
                                                        required
                                                        className="form-control-custom"
                                                        placeholder="Entrez votre identifiant"
                                                    />
                                                </InputGroup>
                                            </Form.Group>

                                            <Form.Group className="mb-4">
                                                <Form.Label className="form-label-custom">
                                                    Mot de passe
                                                </Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text className="input-icon-group">
                                                        <FaLock />
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        type={showPassword ? "text" : "password"}
                                                        name="password"
                                                        value={credentials.password}
                                                        onChange={handleChange}
                                                        required
                                                        className="form-control-custom"
                                                        placeholder="Entrez votre mot de passe"
                                                    />
                                                    <InputGroup.Text 
                                                        className="input-icon-group password-toggle"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                    </InputGroup.Text>
                                                </InputGroup>
                                            </Form.Group>

                                            <div className="form-options mb-4">
                                                <Form.Check 
                                                    type="checkbox" 
                                                    label="Se souvenir de moi"
                                                    className="remember-me"
                                                />
                                                <Link to="/forgot-password" className="forgot-password">
                                                    Mot de passe oublié ?
                                                </Link>
                                            </div>

                                            <Button
                                                type="submit"
                                                className="btn-auth-submit w-100"
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Connexion en cours...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaBolt className="me-2" />
                                                        Se connecter
                                                    </>
                                                )}
                                            </Button>
                                        </Form>

                                        <div className="auth-footer">
                                            <p>
                                                Vous n'avez pas de compte ?{' '}
                                                <Link to="/register" className="auth-link">
                                                    Inscrivez-vous gratuitement
                                                </Link>
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Login;